import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../components/ui/Button/Button';
import {
  SuccessCard, SuccessTitle, SuccessInfoPanel, SuccessInfoRow, SuccessInfoLabel,
  SuccessInfoValue, SuccessNumero, SuccessMessage, SuccessButtonGroup
} from '../../../../components/ui/SuccessCard/SuccessCard';
import { FaCheck, FaFileAlt, FaHome, FaSearch } from 'react-icons/fa';
import { formatCurrency } from '../services/pagamentoService';
import CodigoCopiavel from './CodigoCopiavel';

const COR_STATUS_ENVIADO = '#16a34a';
const COR_STATUS_AGUARDANDO = '#2563eb';

const FORMA_PAGAMENTO_LABEL = { BOLETO: 'Boleto', PIX: 'PIX' };

// dataEnvio vem de toLocaleString('pt-BR') ("25/09/2026, 09:11:13")
const separarDataHora = (dataEnvio) => {
  const [data, hora] = String(dataEnvio || '').split(', ');
  return { data: data || new Date().toLocaleDateString('pt-BR'), hora: hora || new Date().toLocaleTimeString('pt-BR') };
};

// Mesmo formato da tela final do Protocolo Online (componente SuccessCard).
// Pedidos com taxa nunca geram Protocolo final por aqui — o requerimento fica
// em "Aguardando Pagamento" (situacao 3) até a confirmação ser feita à parte,
// então não existe um estado "pagamento confirmado" pra mostrar nesta tela.
// numeroPedido é o SolicitacaoServico.Protocolo (`protocoloNumero` do /criar) — o
// mesmo "Nº do Pedido" de Minhas Solicitações (RequestCard) e de Pendências. Não é o
// ID da SolicitacaoServico (uso interno da API) nem o NumeroRequerimento.
const SuccessScreen = ({ protocoloData, pedidoName, numeroPedido, onNovoRequerimento }) => {
  const navigate = useNavigate();
  const pagamento = protocoloData?.pagamento;
  const comTaxa = pagamento?.valor > 0 && !pagamento?.isento;
  const { data, hora } = separarDataHora(protocoloData?.dataEnvio);

  return (
    <SuccessCard>
      <SuccessTitle>
        <FaCheck style={{ marginRight: '0.5rem' }} />
        Requerimento Enviado com Sucesso!
      </SuccessTitle>

      <SuccessInfoPanel>
        {pedidoName && (
          <SuccessInfoRow>
            <SuccessInfoLabel>Pedido:</SuccessInfoLabel>
            <SuccessInfoValue>{pedidoName}</SuccessInfoValue>
          </SuccessInfoRow>
        )}
        {numeroPedido && (
          <SuccessInfoRow>
            <SuccessInfoLabel>Nº do Pedido:</SuccessInfoLabel>
            <SuccessNumero>{numeroPedido}</SuccessNumero>
          </SuccessInfoRow>
        )}
        {protocoloData?.protocoloNumero && (
          <SuccessInfoRow>
            <SuccessInfoLabel>Nº do Protocolo:</SuccessInfoLabel>
            <SuccessNumero>{protocoloData.protocoloNumero}</SuccessNumero>
          </SuccessInfoRow>
        )}
        <SuccessInfoRow>
          <SuccessInfoLabel>Data de Emissão:</SuccessInfoLabel>
          <SuccessInfoValue>{data}</SuccessInfoValue>
        </SuccessInfoRow>
        <SuccessInfoRow>
          <SuccessInfoLabel>Horário:</SuccessInfoLabel>
          <SuccessInfoValue>{hora}</SuccessInfoValue>
        </SuccessInfoRow>
        {comTaxa && (
          <SuccessInfoRow>
            <SuccessInfoLabel>Valor da Taxa:</SuccessInfoLabel>
            <SuccessInfoValue>
              {formatCurrency(pagamento.valor)}
              {FORMA_PAGAMENTO_LABEL[pagamento.tipo] && ` · ${FORMA_PAGAMENTO_LABEL[pagamento.tipo]}`}
            </SuccessInfoValue>
          </SuccessInfoRow>
        )}
        <SuccessInfoRow>
          <SuccessInfoLabel>Status:</SuccessInfoLabel>
          {comTaxa ? (
            <SuccessInfoValue $cor={COR_STATUS_AGUARDANDO}>Aguardando pagamento</SuccessInfoValue>
          ) : (
            <SuccessInfoValue $cor={COR_STATUS_ENVIADO}>✓ Encaminhado para análise</SuccessInfoValue>
          )}
        </SuccessInfoRow>
      </SuccessInfoPanel>

      {comTaxa && pagamento?.tipo === 'BOLETO' && (
        <SuccessInfoPanel>
          <CodigoCopiavel
            label="Linha digitável"
            valor={pagamento.linhaDigitavel}
            linkHref={pagamento.urlBoleto}
            linkLabel="Abrir boleto em PDF"
          />
        </SuccessInfoPanel>
      )}

      {comTaxa && pagamento?.tipo === 'PIX' && (
        <SuccessInfoPanel>
          <CodigoCopiavel label="PIX Copia e Cola" valor={pagamento.pixCopiaECola} qrCode />
        </SuccessInfoPanel>
      )}

      {/* Com taxa ainda não existe protocolo (só o requerimento, em Aguardando
          Pagamento) — por isso o acompanhamento é em "Minhas Solicitações", e
          não em "Consulta de Protocolos". */}
      <SuccessMessage>
        {comTaxa
          ? 'O número de protocolo será emitido após a confirmação do pagamento. Acompanhe em "Minhas Solicitações".'
          : 'O requerimento foi registrado com sucesso. Acompanhe o andamento em "Consulta de Protocolos".'}
      </SuccessMessage>

      <SuccessButtonGroup>
        {comTaxa ? (
          <Button
            variant="secondary"
            onClick={() => navigate('/solicitacoes')}
            leftIcon={<FaFileAlt />}
          >
            Minhas Solicitações
          </Button>
        ) : (
          <Button
            variant="secondary"
            onClick={() => navigate('/consulta-protocolos')}
            leftIcon={<FaSearch />}
          >
            Consulta de Protocolos
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={() => navigate('/')}
          leftIcon={<FaHome />}
        >
          Voltar para Home
        </Button>
        <Button
          variant="primary"
          onClick={onNovoRequerimento}
          leftIcon={<FaFileAlt />}
        >
          Novo Requerimento
        </Button>
      </SuccessButtonGroup>
    </SuccessCard>
  );
};

export default SuccessScreen;
